import type { Href } from "expo-router";
import { router } from "expo-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { Animated, Easing, Text, View } from "react-native";

import { isAdminRole, signOutToHome } from "../../../lib/auth";
import { useAuth } from "../../../lib/authContext";
import { useMarketingFonts } from "../../../lib/useMarketingFonts";
import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { AdminButton } from "../../../components/admin/ui";
import { StatusPill } from "../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;
const POLL_MS = 30_000;

function Card({ children }: { children: React.ReactNode }) {
  return (
    <View style={{ maxWidth: 420, width: "100%", padding: 24, borderRadius: RADII.lg, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border, alignItems: "center" }}>
      {children}
    </View>
  );
}

export default function AdminPendingScreen() {
  const fontsReady = useMarketingFonts();
  const { user, role, employeeStatus, loading: authLoading, refetchProfile } = useAuth();
  const [checking, setChecking] = useState(false);
  const spin = useRef(new Animated.Value(0)).current;
  const pulse = useRef(new Animated.Value(1)).current;
  const initialStatus = useRef<typeof employeeStatus | undefined>(undefined);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      await refetchProfile();
    } finally {
      setChecking(false);
    }
  }, [refetchProfile]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      router.replace("/login" as Href);
      return;
    }
    if (!isAdminRole(role)) {
      router.replace("/" as Href);
      return;
    }
    if (employeeStatus === "rejected") {
      router.replace("/admin/rejected" as Href);
      return;
    }
    if (initialStatus.current === undefined) {
      initialStatus.current = employeeStatus ?? undefined;
    }
    if (employeeStatus === "active" && initialStatus.current !== "pending") {
      router.replace("/admin" as Href);
    }
  }, [authLoading, user, role, employeeStatus]);

  useEffect(() => {
    if (employeeStatus !== "pending") return;
    const id = setInterval(() => {
      void check();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [employeeStatus, check]);

  useEffect(() => {
    const spinLoop = Animated.loop(
      Animated.timing(spin, { toValue: 1, duration: 18000, easing: Easing.linear, useNativeDriver: true })
    );
    spinLoop.start();
    return () => spinLoop.stop();
  }, [spin]);

  useEffect(() => {
    const p = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 0.25, duration: 1100, useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 1, duration: 1100, useNativeDriver: true }),
      ])
    );
    p.start();
    return () => p.stop();
  }, [pulse]);

  const spinInterpolate = spin.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] });

  if (!fontsReady || authLoading) {
    return <View style={{ flex: 1, backgroundColor: THEME.pageBg }} />;
  }

  const displayName = user?.user_metadata?.full_name as string | undefined;
  const name = displayName?.trim() || user?.email?.split("@")[0] || "there";
  const email = user?.email ?? "";

  if (employeeStatus === "active" && initialStatus.current === "pending") {
    return (
      <View style={{ flex: 1, backgroundColor: THEME.pageBg, alignItems: "center", justifyContent: "center", padding: SPACING.lg }}>
        <Card>
          <StatusPill theme={THEME} label="Account activated" tone="sage" />
          <Text style={{ marginTop: 12, fontFamily: THEME.bodyFont, fontSize: 13, color: THEME.textOnSurfaceDim, textAlign: "center" }}>
            Your account has been approved.
          </Text>
          <View style={{ marginTop: 16 }}>
            <AdminButton theme={THEME} label="Continue to dashboard" onPress={() => router.replace("/admin" as Href)} />
          </View>
        </Card>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: THEME.pageBg, alignItems: "center", justifyContent: "center", padding: SPACING.lg }}>
      <Card>
        <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: THEME.accent }}>
          Employee portal
        </Text>
        <Text style={{ marginTop: 6, fontFamily: THEME.displayMediumFont, fontSize: 24, lineHeight: 28, color: THEME.textOnSurface, textAlign: "center" }}>
          Awaiting activation
        </Text>
        <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 13, color: THEME.textOnSurfaceDim, textAlign: "center" }}>
          {name}, your account is pending super admin approval.
        </Text>

        <View style={{ width: 160, height: 160, marginVertical: 16, alignItems: "center", justifyContent: "center" }}>
          <Animated.View
            style={{
              position: "absolute",
              width: 160,
              height: 160,
              borderRadius: 80,
              borderWidth: STROKES.hairline,
              borderColor: THEME.successBorderSoft,
              transform: [{ rotate: spinInterpolate }],
            }}
          />
          <Animated.View
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: THEME.accentSoft,
              borderWidth: STROKES.border,
              borderColor: THEME.accentBorderHover,
              alignItems: "center",
              justifyContent: "center",
              opacity: pulse,
            }}
          >
            <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: THEME.accent }} />
          </Animated.View>
        </View>

        <StatusPill theme={THEME} label={email} tone="grey" showDot={false} />

        <Text style={{ marginTop: 20, marginBottom: 10, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }}>
          Checking every 30 seconds…
        </Text>
        <AdminButton theme={THEME} label="Check now" variant="outline" size="sm" loading={checking} onPress={() => void check()} />
        <View style={{ marginTop: 10 }}>
          <AdminButton theme={THEME} label="Sign out" variant="ghost" size="sm" onPress={() => void signOutToHome(router)} />
        </View>
      </Card>
    </View>
  );
}
